/**
 * Reference documents admin — SOPs, CPT sheets, payer cheat-sheets.
 *
 * Supervisors upload files here; the agent runtime pulls them into
 * phase context via `list_reference_docs` / `fetch_reference_doc`
 * tool calls at workflow-run time. No PHI in these files by process —
 * the UI carries a plain reminder; enforcement is manual.
 *
 * Loading / empty / error / permission-denied states per Rule Zero #8.
 */
import { useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  Eye,
  FileText,
  Loader2,
  Trash2,
  Upload,
  X,
} from "lucide-react";

import { ENTITIES } from "@/lib/mockData";
import { ApiError } from "@/lib/apiClient";

import {
  ReferenceDoc,
  useDeleteReferenceDoc,
  useReferenceDocContent,
  useReferenceDocs,
  useUploadReferenceDoc,
} from "./api";

const INPUT_CLS =
  "w-full rounded-md border border-paper-line bg-paper-0 px-2 py-1.5 text-sm text-ink-1";


function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}


function formatRelative(iso: string | null): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "—";
  const deltaSec = (Date.now() - t) / 1000;
  if (deltaSec < 90) return "just now";
  if (deltaSec < 3600) return `${Math.round(deltaSec / 60)}m ago`;
  if (deltaSec < 86_400) return `${Math.round(deltaSec / 3600)}h ago`;
  if (deltaSec < 86_400 * 7) return `${Math.round(deltaSec / 86_400)}d ago`;
  return new Date(iso).toLocaleDateString();
}


export function ReferenceDocsScreen() {
  const [entityFilter, setEntityFilter] = useState<string>("all");
  const [tagFilter, setTagFilter] = useState<string>("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useReferenceDocs({
    entity: entityFilter === "all" ? undefined : entityFilter,
    tag: tagFilter.trim() || undefined,
  });
  const deleteDoc = useDeleteReferenceDoc();

  const docs = useMemo(() => data?.items ?? [], [data]);
  const stats = useMemo(() => {
    const withText = docs.filter((d) => d.hasExtractedText).length;
    return { total: docs.length, withText, metadataOnly: docs.length - withText };
  }, [docs]);

  const flashMessage = (text: string) => {
    setFlash(text);
    window.setTimeout(() => setFlash((f) => (f === text ? null : f)), 3500);
  };

  const handleDelete = (id: string) => {
    deleteDoc.mutate(id, {
      onSuccess: () => {
        setPendingDelete(null);
        flashMessage("Document removed.");
      },
      onError: (err) => flashMessage(`Delete failed: ${(err as Error).message}`),
    });
  };

  return (
    <div className="flex h-full flex-col overflow-hidden bg-paper-1">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-paper-line bg-paper-0 px-6 py-4">
        <div>
          <h1 className="text-lg font-semibold text-ink-1">Reference library</h1>
          <p className="text-xs text-ink-3">
            {stats.total} total · {stats.withText} with extracted text ·{" "}
            {stats.metadataOnly} metadata-only
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value)}
            className="rounded-md border border-paper-line bg-paper-0 px-2 py-1.5 text-sm text-ink-1"
            aria-label="Filter by entity"
          >
            <option value="all">All entities</option>
            {ENTITIES.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
          <input
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            placeholder="Tag filter (e.g. cpt)"
            className="w-40 rounded-md border border-paper-line bg-paper-0 px-2 py-1.5 text-sm text-ink-1"
          />
          <button
            type="button"
            onClick={() => setUploadOpen(true)}
            className="inline-flex items-center gap-1 rounded-md bg-ink-1 px-3 py-1.5 text-sm text-paper-0 hover:bg-ink-0"
          >
            <Upload size={14} />
            Upload
          </button>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto px-6 py-4">
        <div className="mb-3 rounded-md border border-paper-line bg-paper-0 p-3 text-xs text-ink-3">
          <strong className="text-ink-2">PHI-free zone:</strong>{" "}
          reference material only (SOPs, CPT/HCPCS tables, payer cheat-sheets).
          Uploads are indexed for the agent runtime's{" "}
          <code className="rounded bg-paper-1 px-1">fetch_reference_doc</code>{" "}
          tool call.
        </div>

        {isLoading && (
          <div className="flex items-center gap-2 py-12 text-ink-3">
            <Loader2 className="animate-spin" size={18} />
            Loading reference library…
          </div>
        )}

        {error && !isLoading && (
          <div className="flex items-start gap-3 rounded-md border border-status-failed/30 bg-status-failed/5 p-4 text-sm">
            <AlertCircle className="text-status-failed" size={18} />
            <div>
              <div className="font-medium text-ink-1">Couldn't load reference documents.</div>
              <div className="text-ink-3">{(error as Error).message}</div>
              {error instanceof ApiError && error.correlationId && (
                <div className="mt-1 font-mono text-[10px] text-ink-3">
                  {error.correlationId}
                </div>
              )}
              <button
                type="button"
                onClick={() => refetch()}
                className="mt-2 rounded-md border border-paper-line bg-paper-0 px-2 py-1 text-xs"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {!isLoading && !error && docs.length === 0 && (
          <div className="rounded-md border border-dashed border-paper-line p-8 text-center text-ink-3">
            <FileText className="mx-auto mb-2" size={24} />
            No reference documents yet. Upload SOPs or CPT sheets to give the agent domain context.
          </div>
        )}

        {!isLoading && !error && docs.length > 0 && (
          <ul className="divide-y divide-paper-line rounded-md border border-paper-line bg-paper-0">
            {docs.map((d) => (
              <DocRow
                key={d.id}
                doc={d}
                onPreview={() => setPreviewId(d.id)}
                onDelete={() => setPendingDelete(d.id)}
              />
            ))}
          </ul>
        )}
      </main>

      {flash && (
        <div
          role="status"
          className="fixed bottom-6 right-6 rounded-md bg-ink-1 px-4 py-2 text-sm text-paper-0 shadow-lg"
        >
          {flash}
        </div>
      )}

      {uploadOpen && (
        <UploadModal
          onClose={() => setUploadOpen(false)}
          onDone={(msg) => {
            setUploadOpen(false);
            flashMessage(msg);
          }}
        />
      )}

      {previewId && (
        <PreviewModal
          docId={previewId}
          onClose={() => setPreviewId(null)}
        />
      )}

      {pendingDelete && (
        <ConfirmDeleteModal
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => handleDelete(pendingDelete)}
          submitting={deleteDoc.isPending}
        />
      )}
    </div>
  );
}


function DocRow(props: {
  doc: ReferenceDoc;
  onPreview: () => void;
  onDelete: () => void;
}) {
  const { doc } = props;
  return (
    <li className="flex items-center justify-between px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <FileText size={14} className="text-ink-3 shrink-0" aria-hidden />
          <div className="truncate text-sm font-medium text-ink-1">{doc.filename}</div>
          <span className="rounded-sm bg-paper-1 px-1.5 py-0.5 text-[10px] text-ink-3">
            {doc.entityCode}
          </span>
          {doc.hasExtractedText ? (
            <span className="rounded-sm bg-teal-500/10 px-1.5 py-0.5 text-[10px] text-teal-500">
              text indexed
            </span>
          ) : (
            <span className="rounded-sm bg-paper-1 px-1.5 py-0.5 text-[10px] text-ink-3">
              metadata only
            </span>
          )}
        </div>
        {doc.summary && (
          <div className="mt-1 truncate text-xs text-ink-2">{doc.summary}</div>
        )}
        <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-ink-3">
          <span>{formatBytes(doc.sizeBytes)}</span>
          <span>· {doc.contentType}</span>
          <span>· Uploaded {formatRelative(doc.uploadedAt)}</span>
          {doc.tags.length > 0 && (
            <span className="flex gap-1">
              {doc.tags.map((t) => (
                <span key={t} className="rounded-sm bg-paper-1 px-1.5 py-0.5">
                  #{t}
                </span>
              ))}
            </span>
          )}
        </div>
      </div>
      <div className="ml-3 flex items-center gap-2">
        <button
          type="button"
          onClick={props.onPreview}
          disabled={!doc.hasExtractedText}
          className="rounded-md border border-paper-line bg-paper-0 p-1.5 text-ink-2 hover:bg-paper-1 disabled:opacity-40"
          aria-label="Preview extracted text"
        >
          <Eye size={14} />
        </button>
        <button
          type="button"
          onClick={props.onDelete}
          className="rounded-md border border-paper-line bg-paper-0 p-1.5 text-ink-2 hover:bg-paper-1"
          aria-label="Delete"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </li>
  );
}


function UploadModal(props: {
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const upload = useUploadReferenceDoc();
  const [entityCode, setEntityCode] = useState<string>(
    (ENTITIES[0] && ENTITIES[0].id) || "laoss",
  );
  const [tags, setTags] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;
    upload.mutate(
      { entityCode, tags, file },
      {
        onSuccess: () => props.onDone(`Uploaded ${file.name}.`),
        onError: (err) => props.onDone(`Upload failed: ${(err as Error).message}`),
      },
    );
  };

  return (
    <ModalFrame title="Upload reference document" onClose={props.onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Entity">
          <select
            value={entityCode}
            onChange={(e) => setEntityCode(e.target.value)}
            className={INPUT_CLS}
          >
            {ENTITIES.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="File (PDF, XLSX, DOCX, CSV, MD, TXT — 25 MB max)">
          <input
            ref={fileRef}
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            accept=".pdf,.xlsx,.xlsm,.docx,.csv,.tsv,.md,.markdown,.txt"
            className="block w-full text-xs text-ink-2"
            required
          />
        </Field>
        <Field label="Tags (comma-separated, e.g. cpt,medicare,billing)">
          <input
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            className={INPUT_CLS}
            placeholder="cpt,billing"
          />
        </Field>

        {upload.error && (
          <div className="rounded-md bg-status-failed/10 p-2 text-xs text-status-failed">
            {(upload.error as Error).message}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={props.onClose}
            className="rounded-md border border-paper-line bg-paper-0 px-3 py-1.5 text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!file || upload.isPending}
            className="inline-flex items-center gap-1 rounded-md bg-ink-1 px-3 py-1.5 text-sm text-paper-0 disabled:opacity-50"
          >
            {upload.isPending && <Loader2 className="animate-spin" size={14} />}
            Upload
          </button>
        </div>
      </form>
    </ModalFrame>
  );
}


function PreviewModal(props: { docId: string; onClose: () => void }) {
  const { data, isLoading, error } = useReferenceDocContent(props.docId);
  return (
    <ModalFrame title="Extracted text" onClose={props.onClose}>
      {isLoading && (
        <div className="flex items-center gap-2 py-6 text-ink-3">
          <Loader2 className="animate-spin" size={16} />
          Loading…
        </div>
      )}
      {error && (
        <div className="rounded-md bg-status-failed/10 p-2 text-xs text-status-failed">
          {(error as Error).message}
        </div>
      )}
      {data !== undefined && !isLoading && !error && (
        <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded-md border border-paper-line bg-paper-1 p-3 text-xs text-ink-1">
          {data || "[no extracted text]"}
        </pre>
      )}
    </ModalFrame>
  );
}


function ConfirmDeleteModal(props: {
  onCancel: () => void;
  onConfirm: () => void;
  submitting: boolean;
}) {
  return (
    <ModalFrame title="Remove document?" onClose={props.onCancel}>
      <p className="text-sm text-ink-2">
        The file + extracted text will be removed from the library. The agent will stop seeing it at the next job start.
      </p>
      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={props.onCancel}
          className="rounded-md border border-paper-line bg-paper-0 px-3 py-1.5 text-sm"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={props.onConfirm}
          disabled={props.submitting}
          className="inline-flex items-center gap-1 rounded-md bg-status-failed px-3 py-1.5 text-sm text-paper-0 disabled:opacity-50"
        >
          {props.submitting && <Loader2 className="animate-spin" size={14} />}
          Remove
        </button>
      </div>
    </ModalFrame>
  );
}


function Field(props: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs text-ink-3">
      <span>{props.label}</span>
      <div className="mt-1">{props.children}</div>
    </label>
  );
}


function ModalFrame(props: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-1/30 p-4">
      <div className="w-full max-w-lg rounded-lg border border-paper-line bg-paper-0 p-5 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-ink-1">{props.title}</h3>
          <button
            type="button"
            onClick={props.onClose}
            aria-label="Close"
            className="rounded-md p-1 text-ink-3 hover:bg-paper-1"
          >
            <X size={14} />
          </button>
        </div>
        {props.children}
      </div>
    </div>
  );
}
